import type { ReturnedEmployee } from 'app/(main)/people/employees/actions'
import { getEmployeesByQuery } from 'app/(main)/people/employees/actions'
import { useCallback } from 'react'
import {
  type BaseEditor,
  type Descendant,
  Editor,
  type NodeEntry,
  Path,
  type Point,
  Range,
  Transforms,
} from 'slate'

export type MentionsFetcher = (
  query: string,
  top?: number,
) => Promise<ReturnedEmployee[]>

export type MentionsEditor = {
  mentionsPluginInstalled?: true
  suggestionsFetcher: MentionsFetcher
}

const defaultFetcher: MentionsFetcher = async (
  query: string,
  top: number = 10,
) => {
  return await getEmployeesByQuery({ query, top })
}

export const withMentions = Object.assign(
  (editor: BaseEditor) => {
    return withMentions.bind(defaultFetcher)(editor)
  },
  {
    bind(fetcher: MentionsFetcher) {
      return <T extends BaseEditor>(editor: T) => {
        const e = editor as T & Editor
        const { isInline, isVoid, useInitializer } = e

        e.mentionsPluginInstalled = true
        e.suggestionsFetcher = fetcher

        e.isInline = (element) => {
          return element.type === 'mention' ? true : isInline(element)
        }

        e.isVoid = (element) => {
          return element.type === 'mention' ? true : isVoid(element)
        }

        e.useInitializer = function useInitializerFunc({
          decorators,
          ...rest
        }) {
          const ranges = useMentionRange(e)
          return useInitializer({
            decorators: [...decorators, ranges],
            ...rest,
          })
        }

        return e
      }
    },
  },
)

export type Mentionable = {
  name: string
  targetId: string
  targetType: 'Person'
  desc?: Descendant
  iconUrl?: string | null
}

function useMentionRange(editor: Editor) {
  return useCallback(
    (entry: NodeEntry) => {
      const [, path] = entry
      const range = getMentionRange(editor, path)
      return range ? [range] : []
    },
    [editor],
  )
}

export const getMentionRange = (editor: Editor, path: Path): Range | null => {
  if (
    editor.mentionsPluginInstalled &&
    editor.selection &&
    Range.isCollapsed(editor.selection) &&
    Path.equals(editor.selection.anchor.path, path)
  ) {
    const { anchor } = editor.selection
    const start = Editor.start(editor, path)
    const range = { anchor: start, focus: anchor }
    const text = Editor.string(editor, range)
    const match = /@[\w]*$/.exec(text)

    if (match) {
      const mentionStartOffset = anchor.offset - match[0].length
      const mentionStart: Point = { path, offset: mentionStartOffset }

      return { anchor: mentionStart, focus: anchor, mentionQuery: true }
    }
  }
  return null
}

export const insertMention = (editor: Editor, mentionable: Mentionable) => {
  const { selection } = editor
  // Delete @... query before inserting the mention in place
  if (selection) {
    const mentionRange = getMentionRange(editor, selection.anchor.path)

    if (mentionRange) {
      Transforms.select(editor, mentionRange)
      Transforms.delete(editor)
    }
  }
  Transforms.insertNodes(editor, {
    ...mentionable,
    type: 'mention',
    children: [{ text: '' }],
  })
  Transforms.move(editor)
}
